// Dual-write error logging + reporting sink (app2 build only).
//
// When a client-side dual-write mirror to the encrypted follower (api2) FAILS,
// we record a structured, metadata-only error record so it can later be reviewed
// to judge whether the two stacks are drifting. There are TWO sinks:
//
//   (1) SERVER-SIDE (the true "separate file"): best-effort POST of the record to
//       the AUTHORITATIVE api (`/dual-write-errors`), which appends one JSONL line
//       to a dedicated log file. This is the durable, central, reviewable log.
//   (2) CLIENT-SIDE fallback: a bounded ring buffer of the last N records in
//       localStorage, so failures survive even when the report POST itself fails
//       (offline, api down). Reviewable from the browser console.
//
// PRIVACY: records carry ONLY metadata needed to judge drift (method, path, api2
// url, http status / error string, idempotency key, a correlation id). NEVER any
// request body / field values / PII.
//
// This module is only ever exercised in the app2 (dualWriteActive()) build; the
// default build never imports the failure path at runtime.

/** Structured, metadata-only record of a single mirror failure. */
export interface DualWriteErrorRecord {
  /** ISO timestamp (client clock) of when the mirror failed. */
  ts: string;
  /** HTTP method of the mirrored mutation. */
  method: string;
  /** Authoritative request path (e.g. "/fields/123"). No query values logged. */
  path: string;
  /** The api2 URL we tried to mirror to (origin + path; query stripped). */
  api2Url: string;
  /** HTTP status of the failed mirror (0 when it never got a response). */
  status: number;
  /** Short error string (timeout / network / "HTTP 500"), never a body. */
  error: string;
  /** Idempotency key of the mutation, if one was set (helps match drift). */
  idempotencyKey?: string;
  /** Client-generated correlation id so client + server logs can be joined. */
  correlationId: string;
}

/** localStorage key for the bounded client-side ring buffer. */
export const DUAL_WRITE_ERRORS_KEY = 'vid.dualWriteErrors';
/** Max records retained client-side (ring buffer). */
export const DUAL_WRITE_ERRORS_MAX = 50;

/** Strip query + fragment from a URL so we never persist query values. */
function stripQuery(url: string): string {
  try {
    const u = new URL(url);
    return u.origin + u.pathname;
  } catch {
    const i = url.search(/[?#]/);
    return i >= 0 ? url.slice(0, i) : url;
  }
}

/** Generate a short correlation id (best-effort; no crypto requirement). */
function correlationId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `dw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Build a structured, metadata-only error record. Never includes bodies/PII. */
export function buildDualWriteErrorRecord(input: {
  method: string;
  path: string;
  api2Url: string;
  status?: number;
  error: unknown;
  idempotencyKey?: string;
}): DualWriteErrorRecord {
  return {
    ts: new Date().toISOString(),
    method: input.method,
    path: input.path,
    api2Url: stripQuery(input.api2Url),
    status: input.status ?? 0,
    error: String(
      input.error instanceof Error ? input.error.message : input.error,
    ).slice(0, 300),
    idempotencyKey: input.idempotencyKey,
    correlationId: correlationId(),
  };
}

/** Read the current client-side ring buffer (never throws). */
export function readDualWriteErrors(): DualWriteErrorRecord[] {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(DUAL_WRITE_ERRORS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as DualWriteErrorRecord[]) : [];
  } catch {
    return [];
  }
}

/** Push a record onto the bounded ring buffer in localStorage (never throws). */
export function pushDualWriteError(rec: DualWriteErrorRecord): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const buf = readDualWriteErrors();
    buf.push(rec);
    while (buf.length > DUAL_WRITE_ERRORS_MAX) buf.shift();
    localStorage.setItem(DUAL_WRITE_ERRORS_KEY, JSON.stringify(buf));
  } catch {
    /* storage full / unavailable => drop silently; server log is the primary sink */
  }
}

/** Clear the client-side ring buffer (never throws). */
export function clearDualWriteErrors(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(DUAL_WRITE_ERRORS_KEY);
  } catch { /* noop */ }
}

/**
 * A reporter injects the actual authed POST to the authoritative
 * `/dual-write-errors` endpoint. We inject it so this module stays free of an
 * import cycle with http.ts and remains unit-testable with no network.
 */
export type DualWriteErrorReporter = (rec: DualWriteErrorRecord) => Promise<void>;

/**
 * Record a mirror failure through BOTH sinks:
 *   1. console.warn (kept for live debugging)
 *   2. localStorage ring buffer (durable client fallback)
 *   3. best-effort authed POST to the authoritative server log (fire-and-forget
 *      for the REPORT itself — the mirror is what blocks, not this report).
 *
 * NEVER throws: logging must not break the caller.
 */
export function recordDualWriteError(
  rec: DualWriteErrorRecord,
  report?: DualWriteErrorReporter,
): void {
  try {
    console.warn('[dual-write] encrypted follower mirror failed (non-fatal):', rec);
  } catch { /* noop */ }
  pushDualWriteError(rec);
  if (report) {
    // Fire-and-forget: a failed report leaves the record in the localStorage
    // buffer, which is the whole point of the fallback.
    void report(rec).catch(() => { /* stays buffered client-side */ });
  }
}

/**
 * Install the browser console review helpers:
 *   window.__vidDualWriteErrors()      -> DualWriteErrorRecord[]
 *   window.__vidDualWriteErrorsClear() -> void
 * Idempotent + defensive; only meaningful in the app2 build.
 */
export function installDualWriteErrorConsoleHelpers(): void {
  try {
    if (typeof window === 'undefined') return;
    const w = window as unknown as Record<string, unknown>;
    w['__vidDualWriteErrors'] = () => readDualWriteErrors();
    w['__vidDualWriteErrorsClear'] = () => clearDualWriteErrors();
  } catch { /* noop */ }
}
